import dayjs from "lib/dayjs";
import { dayCountFraction } from "lib/dayCount";
import { buildMunicipalCashFlows } from "./municipalBond";

const clamp = (value, low, high) => Math.max(low, Math.min(high, value));

function transitionForNode(index, dx, decay) {
  const meanIndex = index * decay;
  const center = Math.round(meanIndex);
  const eta = clamp(meanIndex - center, -0.5, 0.5);
  return [
    [center - 1, 1 / 6 + (eta * eta) / 2 - eta / 2],
    [center, 2 / 3 - eta * eta],
    [center + 1, 1 / 6 + (eta * eta) / 2 + eta / 2],
  ];
}

function addToMap(map, key, amount) {
  map.set(key, (map.get(key) || 0) + amount);
}

export function buildHullWhiteTree({
  discountFactor,
  maturityYears,
  meanReversion = 0.01,
  volatility = 0.0058,
  stepsPerYear = 12,
}) {
  if (typeof discountFactor !== "function") throw new Error("A discount curve is required");
  if (!(maturityYears > 0)) throw new Error("Maturity must be positive");
  if (!(stepsPerYear > 0)) throw new Error("Steps per year must be positive");
  if (!(volatility >= 0)) throw new Error("Volatility cannot be negative");

  const steps = Math.max(1, Math.ceil(maturityYears * stepsPerYear));
  const dt = maturityYears / steps;
  const decay = Math.exp(-Math.max(0, meanReversion) * dt);
  const variance = meanReversion > 1e-12
    ? (volatility * volatility * (1 - Math.exp(-2 * meanReversion * dt))) / (2 * meanReversion)
    : volatility * volatility * dt;
  // The deterministic zero-volatility case retains a single recombining state.
  const dx = variance > 0 ? Math.sqrt(3 * variance) : 1;
  const states = [new Map([[0, 1]])];
  const transitions = [];
  const shifts = [];

  for (let step = 0; step < steps; step += 1) {
    const current = states[step];
    const targetDiscount = discountFactor((step + 1) * dt);
    let unshifted = 0;
    for (const [index, statePrice] of current) {
      unshifted += statePrice * Math.exp(-(index * dx) * dt);
    }
    const shift = -Math.log(targetDiscount / unshifted) / dt;
    shifts.push(shift);
    const next = new Map();
    const stepTransitions = new Map();
    for (const [index, statePrice] of current) {
      const branches = variance > 0 ? transitionForNode(index, dx, decay) : [[0, 1]];
      stepTransitions.set(index, branches);
      const nodeDiscount = Math.exp(-(shift + index * dx) * dt);
      for (const [child, probability] of branches) {
        addToMap(next, child, statePrice * nodeDiscount * probability);
      }
    }
    transitions.push(stepTransitions);
    states.push(next);
  }

  return { steps, dt, dx, decay, variance, states, transitions, shifts, discountFactor };
}

function mapCashFlowsToSteps(cashFlows, dt, steps) {
  const amounts = Array(steps + 1).fill(0);
  for (const flow of cashFlows) {
    const index = clamp(Math.round(flow.time / dt), 1, steps);
    amounts[index] += flow.amount;
  }
  return amounts;
}

function expectedValue(tree, step, node, nextValues) {
  const branches = tree.transitions[step].get(node) || [];
  let expected = 0;
  for (const [child, probability] of branches) {
    expected += probability * (nextValues.get(child) || 0);
  }
  return expected;
}

function nodeDiscount(tree, step, node, spread) {
  return Math.exp(-(tree.shifts[step] + node * tree.dx + spread) * tree.dt);
}

function nodeDate(settlement, time) {
  return settlement.add(Math.round(time * 365.25), "day");
}

function accruedToRedemption(schedule, redemptionDate, face) {
  let previous = schedule.previousCouponDate;
  let next = schedule.nextCouponDate;
  for (const date of schedule.futureDates) {
    if (date.isAfter(redemptionDate, "day") || date.isSame(redemptionDate, "day")) {
      next = date;
      break;
    }
    previous = date;
  }
  if (redemptionDate.isSame(next, "day")) return schedule.couponAmount;
  const fraction = dayCountFraction(previous, redemptionDate, "US MUNI: 30/360");
  return face * schedule.couponRate * Math.max(0, fraction);
}

function noticeExerciseValues({
  tree,
  decisionStep,
  redemptionStep,
  redemptionAmount,
  cashFlowsByStep,
  spread,
}) {
  let values = new Map(tree.states[redemptionStep].keys().map((node) => [node, redemptionAmount]));
  for (let step = redemptionStep - 1; step >= decisionStep; step -= 1) {
    const current = new Map();
    for (const node of tree.states[step].keys()) {
      const discounted = nodeDiscount(tree, step, node, spread) * expectedValue(tree, step, node, values);
      const intermediateCoupon = step > decisionStep ? cashFlowsByStep[step] : 0;
      current.set(node, discounted + intermediateCoupon);
    }
    values = current;
  }
  return values;
}

function buildCallDiagnostics({
  tree,
  schedule,
  exercisePolicy,
  eligibleRedemptionSteps,
  noticeSteps,
  firstCall,
}) {
  let alive = new Map([[0, 1]]);
  const callProbabilityByStep = new Map();

  for (let step = 0; step < tree.steps; step += 1) {
    const next = new Map();
    const exercisedNodes = exercisePolicy.get(step);
    for (const [node, probabilityMass] of alive) {
      if (exercisedNodes?.has(node)) {
        const redemptionStep = Math.min(tree.steps, step + noticeSteps);
        addToMap(callProbabilityByStep, redemptionStep, probabilityMass);
        continue;
      }
      for (const [child, probability] of tree.transitions[step].get(node) || []) {
        addToMap(next, child, probabilityMass * probability);
      }
    }
    alive = next;
  }

  const maturityProbability = [...alive.values()].reduce((sum, value) => sum + value, 0);
  const callEntries = [...callProbabilityByStep.entries()].sort((a, b) => a[0] - b[0]);
  const eventualCallProbability = callEntries.reduce((sum, [, probability]) => sum + probability, 0);
  const firstEligibleStep = eligibleRedemptionSteps.size
    ? Math.min(...eligibleRedemptionSteps)
    : null;
  const firstCallProbability = firstEligibleStep == null
    ? 0
    : callProbabilityByStep.get(firstEligibleStep) || 0;
  const cumulativeProbability = (yearsAfterFirstCall) => callEntries.reduce((sum, [step, probability]) => {
    const redemptionDate = nodeDate(schedule.settlement, step * tree.dt);
    return redemptionDate.isAfter(firstCall.add(yearsAfterFirstCall, "year"), "day")
      ? sum
      : sum + probability;
  }, 0);
  const expectedRedemptionYears = callEntries.reduce(
    (sum, [step, probability]) => sum + step * tree.dt * probability,
    schedule.cashFlows[schedule.cashFlows.length - 1].time * maturityProbability
  );

  return {
    firstCallProbability,
    callWithinOneYearProbability: cumulativeProbability(1),
    callWithinThreeYearsProbability: cumulativeProbability(3),
    callWithinFiveYearsProbability: cumulativeProbability(5),
    eventualCallProbability,
    maturityProbability,
    expectedRedemptionYears,
    expectedRedemptionDate: schedule.settlement
      .add(Math.round(expectedRedemptionYears * 365.25), "day")
      .format("YYYY-MM-DD"),
  };
}

export function priceHullWhiteBond({
  curve,
  settlementDate,
  maturityDate,
  couponRate,
  frequency = "semiannual",
  dayCount = "US MUNI: 30/360",
  face = 100,
  firstCallDate = null,
  callPrice = 100,
  noticeDays = 30,
  meanReversion = 0.01,
  volatility = 0.0058,
  stepsPerYear = 12,
  spread = 0,
}) {
  const schedule = buildMunicipalCashFlows({
    settlementDate,
    maturityDate,
    couponRate,
    frequency,
    dayCount,
    face,
  });
  const maturityYears = schedule.cashFlows[schedule.cashFlows.length - 1].time;
  const tree = buildHullWhiteTree({
    discountFactor: curve.discountFactor,
    maturityYears,
    meanReversion,
    volatility,
    stepsPerYear,
  });
  const cashFlowsByStep = mapCashFlowsToSteps(schedule.cashFlows, tree.dt, tree.steps);
  let values = new Map(tree.states[tree.steps].keys().map((node) => [node, cashFlowsByStep[tree.steps]]));
  const callable = firstCallDate && dayjs(firstCallDate).isValid();
  const firstCall = callable ? dayjs(firstCallDate).startOf("day") : null;
  const noticeSteps = Math.max(1, Math.round((noticeDays / 365.25) / tree.dt));
  let exercisedNodeCount = 0;
  const exercisePolicy = new Map();
  const eligibleRedemptionSteps = new Set();

  for (let step = tree.steps - 1; step >= 0; step -= 1) {
    const current = new Map();
    const redemptionStep = step + noticeSteps;
    let exerciseValues = null;
    if (callable && redemptionStep <= tree.steps) {
      const redemptionDate = nodeDate(schedule.settlement, redemptionStep * tree.dt);
      if (!redemptionDate.isBefore(firstCall, "day") && redemptionDate.isBefore(schedule.maturity, "day")) {
        eligibleRedemptionSteps.add(redemptionStep);
        const redemptionAmount = callPrice + accruedToRedemption(schedule, redemptionDate, face);
        exerciseValues = noticeExerciseValues({
          tree,
          decisionStep: step,
          redemptionStep,
          redemptionAmount,
          cashFlowsByStep,
          spread,
        });
      }
    }

    for (const node of tree.states[step].keys()) {
      const continuation = nodeDiscount(tree, step, node, spread) * expectedValue(tree, step, node, values);
      let exCouponValue = continuation;
      if (exerciseValues) {
        const exercise = exerciseValues.get(node);
        if (exercise < continuation) {
          exCouponValue = exercise;
          exercisedNodeCount += 1;
          if (!exercisePolicy.has(step)) exercisePolicy.set(step, new Set());
          exercisePolicy.get(step).add(node);
        }
      }
      current.set(node, exCouponValue + cashFlowsByStep[step]);
    }
    values = current;
  }

  const callDiagnostics = callable
    ? buildCallDiagnostics({
      tree,
      schedule,
      exercisePolicy,
      eligibleRedemptionSteps,
      noticeSteps,
      firstCall,
    })
    : null;

  return {
    dirtyPrice: values.get(0),
    cleanPrice: values.get(0) - schedule.accruedInterest,
    accruedInterest: schedule.accruedInterest,
    tree,
    schedule,
    exercisedNodeCount,
    callDiagnostics,
  };
}

export function solveHullWhiteOas({
  cleanPrice,
  lowerBound = -0.1,
  upperBound = 0.1,
  tolerance = 1e-10,
  ...pricingInputs
}) {
  const target = Number(cleanPrice);
  if (!Number.isFinite(target) || target <= 0) throw new Error("Clean price must be positive");
  const price = (spread) => priceHullWhiteBond({ ...pricingInputs, spread }).cleanPrice;
  let low = lowerBound;
  let high = upperBound;
  if (price(low) < target || price(high) > target) throw new Error("OAS root is not bracketed");
  for (let iteration = 0; iteration < 80; iteration += 1) {
    const mid = (low + high) / 2;
    const difference = price(mid) - target;
    if (Math.abs(difference) <= tolerance) return mid;
    if (difference > 0) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

export function analyzeHullWhiteBond(inputs) {
  const oas = solveHullWhiteOas(inputs);
  const callable = priceHullWhiteBond({ ...inputs, spread: oas });
  const nonCallable = priceHullWhiteBond({ ...inputs, firstCallDate: null, spread: oas });
  const bump = 0.0001;
  const down = priceHullWhiteBond({ ...inputs, spread: oas - bump }).dirtyPrice;
  const up = priceHullWhiteBond({ ...inputs, spread: oas + bump }).dirtyPrice;
  const price = callable.dirtyPrice;
  return {
    oas,
    oasBps: oas * 10000,
    cleanPrice: callable.cleanPrice,
    dirtyPrice: price,
    accruedInterest: callable.accruedInterest,
    optionValue: callable.dirtyPrice - nonCallable.dirtyPrice,
    effectiveDuration: (down - up) / (2 * price * bump),
    effectiveConvexity: (down + up - 2 * price) / (price * bump * bump),
    exercisedNodeCount: callable.exercisedNodeCount,
    callDiagnostics: callable.callDiagnostics,
  };
}

